import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useMenuLookups } from "../../../../services/hooks/useMenuLookups";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { RootState } from "../../../../services/redux/app/store";
import {
  selectLoyaltyFlashOffers,
  selectRedeemedLoyaltyOffer,
} from "../../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  selectIsVerifiedOrganizationCustomer,
  selectUseLoyaltyService,
} from "../../../../services/redux/selectors/loyaltyServiceSelectors";
import { redeemOfferAction } from "../../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  dispatchApplyLoyaltyItemOfferFlow,
  getMenuSubItemsForOffer,
} from "../../../../services/redux/thunks/applyLoyaltyItemOfferFlow";
import {
  GraphicTypeEnum,
  LocalizedString,
} from "../../../../services/redux/types/CartLoyaltyV2Types";
import { capitalizeFirstLetter } from "../../../../utils/help/textPreparation/textPreparation";
import Offer from "../../../../utils/OfferClass/Offer/Offer";
import ConfettiComponent from "../../../global/AnimatedConffeti/AnimatedConfettiComponent";
import { MapGeneralOfferIcon } from "../../../global/GeneralOfferIcon/GeneralOfferIcon";
import { OfferIcon } from "../../../global/Icons/loyalty.icons";
import { LoyaltyOfferIcon } from "../../../global/LoyaltyOfferIcon/LoyaltyOfferIcon";
import styles from "./OrganizationCustomerOffer.module.scss";

export const OrganizationCustomerOfferComponent = (props: {
  offerDetails: Offer[];
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();

  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const isVerifiedOrganizationCustomer = useAppSelector(
    selectIsVerifiedOrganizationCustomer
  );
  const loyaltyFlashOffers = useAppSelector(selectLoyaltyFlashOffers);
  const redeemedLoyaltyOffer = useAppSelector(selectRedeemedLoyaltyOffer);
  const store = useStore<RootState>();
  const selectedLanguage = useAppSelector(
    (state) => state.config.selectedLanguage
  );

  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const { subItemLookup } = useMenuLookups();
  const redeemedOffer = cartLoyaltyV2.cart.redeemedOffer;

  const currency = useAppSelector((state) => state.config.currency);
  const currentLegacyOffer = props.offerDetails[0];
  const currentLoyaltyOffer = loyaltyFlashOffers?.[0];

  const loyaltyOrgBanner =
    useLoyaltyService &&
    isVerifiedOrganizationCustomer &&
    !!currentLoyaltyOffer;

  // Track which offer id we've already auto-redeemed so a calculate response that
  // doesn't echo the offer back (org offers, empty/ineligible cart) can't retrigger
  // an endless redeem -> calculate loop.
  const attemptedLegacyOfferIdRef = useRef<string | number | null>(null);
  const attemptedLoyaltyOfferIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (loyaltyOrgBanner) {
      if (!currentLoyaltyOffer || redeemedLoyaltyOffer) {
        return;
      }
      const loyaltyOfferKey = currentLoyaltyOffer.id;
      if (attemptedLoyaltyOfferIdRef.current === loyaltyOfferKey) {
        return;
      }
      attemptedLoyaltyOfferIdRef.current = loyaltyOfferKey;
      dispatchApplyLoyaltyItemOfferFlow(dispatch, store.getState, {
        offer: currentLoyaltyOffer,
        menuSubItems: getMenuSubItemsForOffer(
          currentLoyaltyOffer,
          subItemLookup
        ),
      });

      return;
    }

    if (!currentLegacyOffer || redeemedOffer) {
      return;
    }

    // Don't stack redeems while a calculate is still in flight.
    if (cartLoyaltyV2.isLoading) {
      return;
    }

    const legacyOfferKey = currentLegacyOffer.id;
    if (attemptedLegacyOfferIdRef.current === legacyOfferKey) {
      return;
    }
    attemptedLegacyOfferIdRef.current = legacyOfferKey;
    dispatch(redeemOfferAction({ offer: currentLegacyOffer }));
  }, [
    loyaltyOrgBanner,
    currentLoyaltyOffer?.id,
    currentLegacyOffer?.id,
    redeemedLoyaltyOffer,
    redeemedOffer,
    cartLoyaltyV2.isLoading,
    dispatch,
  ]);

  if (loyaltyOrgBanner && currentLoyaltyOffer) {
    const lang = selectedLanguage as keyof LocalizedString;
    const title =
      currentLoyaltyOffer.getTitle(lang) ||
      currentLoyaltyOffer.getSubTitle(lang);
    const description = currentLoyaltyOffer.getDescription(lang);
    const appliedI18n = t("common:offer-applied");
    const offerKey = currentLoyaltyOffer.shortId ?? currentLoyaltyOffer.id;

    return (
      <div
        className={styles.container}
        data-testid={`organization_customer_offer_loyalty_${offerKey}`}
      >
        <div className={styles.confetti} data-testid="confetti-div">
          <ConfettiComponent />
        </div>
        <span className={styles.offer_icon_background}>
          <LoyaltyOfferIcon
            offer={currentLoyaltyOffer}
            backgroundIconColor="primary"
            iconOnly
            // width={28}
            // fontStyle={{ fontSize: "8px", color: "white", zIndex: 1 }}
          />
        </span>
        <div className={styles.offer_text}>
          {title ? (
            <span>{`${capitalizeFirstLetter(title)} ${appliedI18n}`}</span>
          ) : description ? (
            <span>
              <span dangerouslySetInnerHTML={{ __html: description }} />{" "}
              {appliedI18n}
            </span>
          ) : (
            <span>{appliedI18n}</span>
          )}
        </div>
      </div>
    );
  }

  if (!currentLegacyOffer) {
    return null;
  }

  const getOfferText = () => {
    return (
      <span>{`${currentLegacyOffer.getOrganizationCustomerOfferText(
        currency
      )}`}</span>
    );
  };

  return (
    <div
      className={styles.container}
      data-testid={`organization_customer_offer_${currentLegacyOffer.offerType}`}
    >
      <div className={styles.confetti} data-testid="confetti-div">
        <ConfettiComponent />
      </div>
      <span className={styles.offer_icon_background}>
        <OfferIcon />
        <span className={styles.offer_icon}>
          <MapGeneralOfferIcon
            graphic={
              currentLegacyOffer.uiDetails?.graphic || {
                type: GraphicTypeEnum.TEXT,
                value: [""],
              }
            }
          />
        </span>
      </span>
      <div className={styles.offer_text}>{getOfferText()}</div>
    </div>
  );
};
